<template lang="pug">
include ../../configs/templatesPUG/template
div.w-100.max-width-440
  div.d-flex.justify-center
    a(href="https://edhelp.me" target="_blank")
      v-img(src="@/assets/images/logo-dark.svg" width="48" height="48" alt="logo")
  div.fs-30.font-weight-600.mt-6 {{ $t('login.helloTitle') }}
  div.fs-16.grey_600--text.mt-3.mb-8 {{ $t('login.helloSubtitle') }}

  div.card-shadow.white.br-12.py-8.px-10
    div(v-if="!isAuthStatus").fs-14.font-weight-500.error_600--text.mb-3.transition {{ $t('checkDataCorrection') }}
    +input('body.login', 'login.login', '["required", "email"]')(
      @keyup.enter="authByCredits"
      @focus="isAuthStatus = true")
    +input('body.password', 'login.password', '["required"]')(
      :append-icon="isShowPass ? 'mdi-eye' : 'mdi-eye-off'"
      :type="isShowPass ? 'text' : 'password'"
      @click:append="isShowPass = !isShowPass"
      @keyup.enter="authByCredits"
      @focus="isAuthStatus = true")
    div.d-flex.align-center.justify-space-between.mb-6
      +checkbox('body.remember_me', 'login.rememberMe').mt-0.pt-0
      router-link(:to="{ name: 'forget-password' }").fs-14.font-weight-500.blue_600--text {{ $t('login.forgotPassword.title') }}
    +btn-full-width('blue_600', 'btn.sign')(:loading="isLoad" @click="authByCredits")
    GoogleAuth(:isLoading="isGoogleAuthLoading" @onSuccess="authThirdPartyResources").my-4
    FacebookAuth(:isLoading="isFacebookAuthLoading" @onSuccess="authThirdPartyResources")

  div.text-center.my-8
    span.fs-14.font-weight-400.grey_600--text {{ $t('login.notAvailableAccount') }}
    router-link(:to="{ name: 'registration' }").fs-14.font-weight-500.blue_600--text.ml-1 {{ $t('btn.signUp') }}

  div(v-click-outside="() => isOpenLanguageMenu = false").text-center.position-relative
    span.fs-14.font-weight-400.grey_600--text {{ $t('interfaceLanguage') }}:
    span(@click.stop="isOpenLanguageMenu = !isOpenLanguageMenu").fs-14.font-weight-500.blue_600--text.ml-1.pointer {{ currentLanguage.name }}
      v-icon(color="blue_600" size="20").ml-1 {{ `mdi-chevron-${isOpenLanguageMenu ? 'up' : 'down'}` }}
    div(v-show="isOpenLanguageMenu").languagesList
      div(
        v-for="lang in languagesList"
        :key="lang.value"
        :class="{ 'active': lang.value === currentLanguage.value }"
        @click="handleSelectLanguage(lang.value)").py-2.px-4.fs-14.text-2--text.pointer {{ lang.name }}
</template>

<script>
import { authForm } from '@/configs/validationForm'
import { mapActions, mapState } from 'vuex'
import { logout } from '@ananasbear/auth'

export default {
  name: 'Login',
  components: {
    GoogleAuth: () => import('@/components/modules/GoogleOAuth.vue'),
    FacebookAuth: () => import('@/components/modules/FacebookAuth.vue')
  },
  data () {
    return {
      isShowPass: false,
      isFacebookAuthLoading: false,
      isAuthStatus: true,
      isLoad: false,
      isGoogleAuthLoading: false,
      isOpenLanguageMenu: false,
      body: {
        login: '',
        password: '',
        remember_me: false
      }
    }
  },
  validations () { return authForm(this) },
  computed: {
    ...mapState({
      profile: state => state.user.profile,
      languagesList: state => state.directory.languagesList
    }),
    loginFormat () {
      const regexEmail = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)$/
      return regexEmail.test(this.body.login) ? 'email' : 'phone'
    },
    currentLanguage () {
      const locale = this.$i18n.locale || 'uk'
      return this.languagesList.find(item => item.value === locale)
    }
  },
  mounted () {
    if (this.$route.query.lang) this.$i18n.locale = this.$route.query.lang === 'en' ? 'en-US' : this.$route.query.lang
    document.title = `${ this.$t(this.$route.meta.title) } | Education`
  },
  methods: {
    ...mapActions(['convertToken', 'getProfile', 'getUserInfoFromGoogle', 'getUserInfoFromFacebook', 'setAuth']),

    async authByCredits () {
      if (this.$v.$invalid) return this.$v.$touch()

      const body = {
        password: this.body.password,
        [this.loginFormat]: this.body.login,
        remember_me: this.body.remember_me
      }

      logout()
      this.$store.commit('SET_DATA_USER', { profile: null })

      this.isLoad = true
      const response = await this.setAuth(body)
      this.isLoad = false
      if ([200, 201].includes(response?.code)) {
        await this.setAuthData(response.data.access, body.remember_me ? 604800 : 0)
        window.location = this.$route.query?.callback_url || '/profile'
      } else this.isAuthStatus = false
    },

    async authThirdPartyResources (user, type) { // Auth with Google or Facebook
      let accessToken
      if (type === 'google-oauth2') {
        Object.values(user).forEach(value => { // Object property with access_token change every month by google
          if (typeof value === 'object' && value.access_token) accessToken = value.access_token
        })
        this.isGoogleAuthLoading = true
      } else {
        accessToken = user.accessToken
        this.isFacebookAuthLoading = true
      }

      const convertTokenResponse = await this.convertToken({
        grant_type: 'convert_token',
        client_id: process.env.VUE_APP_BACKEND_CLIENT_ID,
        backend: type,
        token: accessToken
      }).finally(() => {
        this.isGoogleAuthLoading = false
        this.isFacebookAuthLoading = false
      })

      if (convertTokenResponse?.code === 200) {
        logout()
        this.$store.commit('SET_DATA_USER', { profile: null })
        await this.setAuthData(convertTokenResponse.data.access_token, convertTokenResponse.data.expires_in)
        this.getProfile().then(() => {
          if (!this.profile.memberships.length) this.$router.push({ name: 'registration', params: this.profile })
          else window.location = this.$route.query?.callback_url || '/profile'
        })
      } else this.socialCredentialsRegistration(user, type)
    },

    socialCredentialsRegistration (user, type) {
      if (type === 'google-oauth2') {
        Object.values(user).forEach(value => {
          if (typeof value === 'object' && value.access_token) {
            this.getUserInfoFromGoogle({ access_token: value.access_token })
              .then(result => { this.$router.push({
                  name: 'registration',
                  params: {
                    first_name: result.given_name,
                    last_name: result.family_name,
                    email: result.email
                  }
                })
              }
            )
          }
        })
      } else {
        this.getUserInfoFromFacebook({ access_token: user.accessToken })
          .then(result => { this.$router.push({ name: 'registration', params: result }) })
      }
    },

    setAuthData (token, expiredIn) {
      localStorage.setItem('TOKEN', `Bearer ${ token }`)
      if (expiredIn) {
        const now = new Date()
        now.setTime(now.getTime() + (expiredIn * 1000))
        document.cookie = `TOKEN=Bearer ${ token }; expires=${ now.toUTCString() }; samesite=strict;`
      }
      else document.cookie = `TOKEN=Bearer ${ token }; samesite=strict; max-age=28800;`
    },

    handleSelectLanguage (lang) {
      if (localStorage.getItem('LOCALE') === lang) return false // Don't reload page if necessary locale is already set
      localStorage.setItem('LOCALE', lang)
      // Reload same page without query params
      location.replace(location.origin + location.pathname)
    }
  }
}
</script>

<style lang="sass" scoped>
.languagesList
  position: absolute
  right: 110px
  top: 30px
  @media (max-height: 1060px)
    top: -110px

  height: fit-content
  background: #FFFFFF
  box-shadow: 0 4px 6px -2px rgba(16, 24, 40, 0.05), 0 12px 16px -4px rgba(16, 24, 40, 0.10)
  border-radius: 8px
  transition: 0.4s
  overflow: hidden
  z-index: 2
  & > div
    &.active
      background: #F8F9FC
      color: #3E4784
</style>
